import {
  BulkUploadColumnName,
  BulkUploadItemFieldNames,
  BulkUploadPackageFieldNames,
} from "enum/bulk-upload-column-names.enum";
import { CarrierErrorCodes } from "enum/error-codes/carrier-error-codes.enum";
import { CarrierLabelErrorDictionary } from "enum/error-codes/carrier-label-error-codes.enum";
import { GenericErrorCodes } from "enum/error-codes/generic-error-codes.enum";
import { RuleErrorCodes } from "enum/error-codes/rule-error-codes.enum";
import { ShipmentConsignmentErrorCodes } from "enum/error-codes/shipment-consignment-error-codes.enum";
import { ShipmentItemErrorCodes } from "enum/error-codes/shipment-item-error-codes.enum";
import { ZoneErrorCodes } from "enum/error-codes/zone-error-codes.enum";
import { statuses } from "enum/shipment-statuses.enum";
import {
  IShipment,
  IShipmentItemDescription,
  ShipmentErrors,
} from "models/shipment/shipment.model";
import { IShipmentPackage } from "models/shipmentPackage/shipmentPackage.model";
import { PackageErrorCodes } from "enum/error-codes/package-error-codes.enum";
import { PopalErrorCodes } from "enum/error-codes/popal-error-codes.enum";
import {
  mapPopalErrorForUI,
  isPopalError,
} from "services/error-mapping/popal-error-mapping.service";
import { humanisePackageLimitError } from "services/error-mapping/package-limit-error-mapping";

export const isShipmentError = (fieldName: BulkUploadColumnName) => {
  return (
    fieldName === BulkUploadColumnName.customerReference ||
    fieldName === BulkUploadColumnName.reasonForExport ||
    fieldName === BulkUploadColumnName.fromAddressFirstName ||
    fieldName === BulkUploadColumnName.fromAddressLastName ||
    fieldName === BulkUploadColumnName.fromAddressCompany ||
    fieldName === BulkUploadColumnName.fromAddressPhone ||
    fieldName === BulkUploadColumnName.fromAddressEmail ||
    fieldName === BulkUploadColumnName.fromAddressStreet1 ||
    fieldName === BulkUploadColumnName.fromAddressStreet2 ||
    fieldName === BulkUploadColumnName.fromAddressStreet3 ||
    fieldName === BulkUploadColumnName.fromAddressCity ||
    fieldName === BulkUploadColumnName.fromAddressCountyState ||
    fieldName === BulkUploadColumnName.fromAddressZip ||
    fieldName === BulkUploadColumnName.fromAddressCountryIso ||
    fieldName === BulkUploadColumnName.toAddressFirstName ||
    fieldName === BulkUploadColumnName.toAddressLastName ||
    fieldName === BulkUploadColumnName.toAddressCompany ||
    fieldName === BulkUploadColumnName.toAddressPhone ||
    fieldName === BulkUploadColumnName.toAddressEmail ||
    fieldName === BulkUploadColumnName.toAddressStreet1 ||
    fieldName === BulkUploadColumnName.toAddressStreet2 ||
    fieldName === BulkUploadColumnName.toAddressStreet3 ||
    fieldName === BulkUploadColumnName.toAddressCity ||
    fieldName === BulkUploadColumnName.toAddressCountyState ||
    fieldName === BulkUploadColumnName.toAddressZip ||
    fieldName === BulkUploadColumnName.toAddressCountryIso
  );
};

export const isPackageError = (fieldName: BulkUploadPackageFieldNames) => {
  return (
    fieldName === BulkUploadPackageFieldNames.packageType ||
    BulkUploadPackageFieldNames.packageWidth ||
    BulkUploadPackageFieldNames.packageHeight ||
    BulkUploadPackageFieldNames.packageLength ||
    BulkUploadPackageFieldNames.packageWeight
  );
};

export const isItemError = (fieldName: BulkUploadItemFieldNames) => {
  return (
    fieldName === BulkUploadItemFieldNames.itemDescription ||
    fieldName === BulkUploadItemFieldNames.itemOriginCountryIso ||
    fieldName === BulkUploadItemFieldNames.itemQuantity ||
    fieldName === BulkUploadItemFieldNames.itemValue ||
    fieldName === BulkUploadItemFieldNames.itemValueCurrency ||
    fieldName === BulkUploadItemFieldNames.itemSku ||
    fieldName === BulkUploadItemFieldNames.itemHsCode ||
    fieldName === BulkUploadItemFieldNames.itemWeight ||
    fieldName === BulkUploadItemFieldNames.manufacturerDetails ||
    fieldName === BulkUploadItemFieldNames.itemManufacturerIdentifier ||
    fieldName === BulkUploadItemFieldNames.itemStandardisedIdentifier
  );
};

export type ShipmentErrorSectionKind =
  | "consignment"
  | "label"
  | "rule"
  | "zone"
  | "carrier"
  | "package";

export type ShipmentErrorSection = {
  kind: ShipmentErrorSectionKind;
  title: string;
  messages: string[];
};

const getConsignmentErrorMessages = (shipment: IShipment): string[] => {
  return Object.keys(shipment.errors.consignment).map((fieldName) => {
    const fieldErrors = shipment.errors.consignment[fieldName];
    const errorCode = Array.isArray(fieldErrors)
      ? fieldErrors[0]?.code
      : fieldErrors?.code;

    if (isShipmentError(fieldName as BulkUploadColumnName)) {
      return `${fieldName}: ${
        PrintConsignmentShipmentErrors(errorCode, shipment[fieldName]) ??
        UNHANDLED_ERROR_MESSAGE
      }`;
    }

    if (isItemError(fieldName as BulkUploadItemFieldNames)) {
      return (
        PrintConsignmentItemErrors(errorCode, shipment.itemDescriptions?.[0]) ??
        UNHANDLED_ERROR_MESSAGE
      );
    }

    return UNHANDLED_ERROR_MESSAGE;
  });
};

const getLabelErrorMessages = (packages: IShipmentPackage[]): string[] => {
  const packageErrors: string[] = [];
  const addUnique = (message?: string) => {
    if (message && !packageErrors.includes(message)) {
      packageErrors.push(message);
    }
  };

  packages?.forEach((p) => {
    p?.errors?.forEach((e) => {
      if (!e) return;

      if (e.code === "INVALID_HS_CODE") {
        addUnique("HS code was rejected by the carrier.");
      } else if (e.code === "INVALID_EORI_NUMBER") {
        addUnique("EORI number was rejected by the carrier.");
      } else if (e.raw?.some((r) => r?.message)) {
        e.raw.forEach((r) => addUnique(r?.message));
      } else {
        // Fallback: try translating code using PrintLabelErrors
        const fallbackMessage = PrintLabelErrors(e.code);
        if (fallbackMessage && fallbackMessage !== UNHANDLED_ERROR_MESSAGE) {
          addUnique(fallbackMessage);
        } else if (e.code) {
          // If PrintLabelErrors can't handle it, show the raw error code
          addUnique(`Error: ${e.code}`);
        }
      }
    });
  });

  return packageErrors.map((error) => PrintLabelErrors("", error));
};

const getPackageErrorMessages = (shipmentErrors: ShipmentErrors): string[] => {
  const messages: string[] = [];
  const seen = new Set<string>();
  const addUnique = (message?: string) => {
    if (message && !seen.has(message)) {
      seen.add(message);
      messages.push(message);
    }
  };

  const itemExceedsPackageString =
    PackageErrorCodes.ITEM_WEIGHT_EXCEEDS_PACKAGE_WEIGHT.toLowerCase().replaceAll(
      "_",
      " ",
    );
  const itemExceedsPackageMessage =
    itemExceedsPackageString.charAt(0).toUpperCase() +
    itemExceedsPackageString.slice(1) +
    ".";

  shipmentErrors.packages?.forEach((p) => {
    p?.errors?.limits?.forEach((packageError) => {
      // Stored code is the whole sentence; older rows still start with the
      // enum name, which must never be shown to the customer.
      addUnique(humanisePackageLimitError(packageError?.code));
    });

    // Field-level errors (packageWeight, packageHeight, ...) — e.g. the
    // total item weight exceeding the package weight lands on packageWeight
    Object.keys(p?.errors ?? {}).forEach((field) => {
      if (field === "limits") return;
      const fieldErrors = p.errors[field];
      if (!Array.isArray(fieldErrors)) return;

      fieldErrors.forEach((packageError) => {
        if (!packageError?.code) return;
        addUnique(
          packageError.code ===
            PackageErrorCodes.ITEM_WEIGHT_EXCEEDS_PACKAGE_WEIGHT
            ? itemExceedsPackageMessage
            : `${field}: ${packageError.code}`,
        );
      });
    });

    p?.packageItems?.forEach((item) => {
      Object.keys(item?.errors ?? {}).forEach((field) => {
        const fieldErrors = item.errors[field];
        const code = Array.isArray(fieldErrors)
          ? fieldErrors[0]?.code
          : fieldErrors?.code;
        if (!code) return;

        if (field === "itemOriginCountryIso" && code === "INVALID_STRING") {
          addUnique("Invalid value");
        } else if (
          field === "itemOriginCountryIso" &&
          code === "INVALID_COUNTRY"
        ) {
          addUnique("Invalid item country of origin provided");
        } else if (
          code === PackageErrorCodes.ITEM_WEIGHT_EXCEEDS_PACKAGE_WEIGHT
        ) {
          addUnique(itemExceedsPackageMessage);
        } else {
          addUnique(`${field}: ${code}`);
        }
      });
    });
  });

  return messages;
};

// Malformed error data must never crash the errors column (which has no
// error boundary around it) or blank a panel section. Sections are only
// built when their has*Errors check passed, so an empty or thrown result
// means "errors exist but couldn't be rendered" — show the generic
// message rather than nothing.
const safeMessages = (buildMessages: () => string[]): string[] => {
  try {
    const messages = buildMessages().filter(
      (message) => typeof message === "string" && message.length > 0,
    );
    return messages.length > 0 ? messages : [UNHANDLED_ERROR_MESSAGE];
  } catch {
    return [UNHANDLED_ERROR_MESSAGE];
  }
};

/**
 * Single source of truth for shipment error messages. Both the details
 * panel (<ShipmentErrors />) and the table errors column
 * (getFirstShipmentError) derive from this, so they can never disagree.
 * Sections are ordered exactly as the panel displays them.
 */
export const getShipmentErrorSections = (
  shipment: IShipment | null,
): ShipmentErrorSection[] => {
  if (!shipment) return [];

  const sections: ShipmentErrorSection[] = [];
  const { errors, packages } = shipment;

  // Errors describe why a label could not be produced, but the server never
  // clears them when a shipment recovers (manually selected quote, or a rerun
  // on a different service). That includes package label errors: a failed
  // carrier response marks the whole shipment status "error", and a later
  // successful attempt writes the label fields without clearing errors — so a
  // shipment can only be label_created once every package has a label, and
  // any error still attached to it is stale by definition. Hide them all.
  const isLabelled = shipment.status === statuses.label_created;

  if (!isLabelled && errors && hasConsignmentErrors(errors)) {
    sections.push({
      kind: "consignment",
      title: "Consignment errors",
      messages: safeMessages(() => getConsignmentErrorMessages(shipment)),
    });
  }

  if (!isLabelled && hasLabelErrors(packages)) {
    sections.push({
      kind: "label",
      title: "Label errors",
      messages: safeMessages(() => getLabelErrorMessages(packages)),
    });
  }

  if (!isLabelled && errors && hasRuleErrors(errors)) {
    sections.push({
      kind: "rule",
      title: "Rule errors",
      messages: safeMessages(() =>
        errors.rule.map(
          (error) => PrintRuleErrors(error?.code) ?? UNHANDLED_ERROR_MESSAGE,
        ),
      ),
    });
  }

  if (!isLabelled && errors && hasZoneErrors(errors)) {
    sections.push({
      kind: "zone",
      title: "Zone errors",
      messages: safeMessages(() =>
        errors.zone.map(
          (error) => PrintZoneErrors(error?.code) ?? UNHANDLED_ERROR_MESSAGE,
        ),
      ),
    });
  }

  if (!isLabelled && errors && hasCarrierErrors(errors)) {
    sections.push({
      kind: "carrier",
      title: "Carrier errors",
      messages: safeMessages(() =>
        errors.carrier.map(
          (error) => PrintCarrierErrors(error?.code) ?? UNHANDLED_ERROR_MESSAGE,
        ),
      ),
    });
  }

  if (!isLabelled && errors && hasPackageErrors(errors)) {
    sections.push({
      kind: "package",
      title: "Package errors",
      messages: safeMessages(() => getPackageErrorMessages(errors)),
    });
  }

  return sections;
};

export const getFirstShipmentError = (shipment: IShipment): string => {
  const firstSectionWithMessage = getShipmentErrorSections(shipment).find(
    (section) => section.messages.length > 0,
  );

  return firstSectionWithMessage ? firstSectionWithMessage.messages[0] : "";
};

export const UNHANDLED_ERROR_MESSAGE = "Please try again.";

export const PrintLabelErrors = (
  errorCode: CarrierLabelErrorDictionary | string,
  errorMessage?: string,
) => {
  // Check if this is a Popal error first
  if (errorMessage && isPopalError(errorMessage)) {
    const mappedError = mapPopalErrorForUI(errorMessage);
    return mappedError.userMessage;
  }

  // Handle known label error codes
  switch (errorCode) {
    case CarrierLabelErrorDictionary.INVALID_EORI_NUMBER:
      return "EORI number was rejected by the carrier.";

    case CarrierLabelErrorDictionary.INVALID_HS_CODE:
      return "HS code was rejected by the carrier.";

    // Handle Popal error codes
    case PopalErrorCodes.SERVICE_UNAVAILABLE:
    case PopalErrorCodes.SERVICE_TIMEOUT:
    case PopalErrorCodes.CONNECTION_RESET:
    case PopalErrorCodes.CONNECTION_ERROR:
    case PopalErrorCodes.DATABASE_TIMEOUT:
      return "The shipping service is temporarily unavailable. Please try again in a few minutes.";

    case PopalErrorCodes.PHONE_REQUIRED:
    case PopalErrorCodes.PHONE_EMPTY:
      return "Phone number is required for the delivery address.";

    case PopalErrorCodes.PHONE_INVALID:
      return "Please provide a valid phone number for the delivery address.";

    case PopalErrorCodes.WEIGHT_REQUIRED:
      return "Package weight is required.";

    case PopalErrorCodes.INVALID_POSTCODE:
      return "Please provide a valid UK postcode.";

    case PopalErrorCodes.COMMODITY_CODE_REQUIRED:
      return "Shipments to Northern Ireland require valid commodity codes for all items.";

    default:
      // If we have an error message, try to map it as a Popal error
      if (errorMessage) {
        const mappedError = mapPopalErrorForUI(errorMessage);
        if (mappedError.errorCode !== PopalErrorCodes.UNKNOWN_ERROR) {
          return mappedError.userMessage;
        }
      }
      return errorMessage || UNHANDLED_ERROR_MESSAGE;
  }
};

export const PrintConsignmentItemErrors = (
  errorCode:
    | ShipmentItemErrorCodes
    | GenericErrorCodes
    | ShipmentConsignmentErrorCodes,
  item: IShipmentItemDescription,
) => {
  switch (errorCode) {
    case GenericErrorCodes.FIELD_REQUIRED:
      return `this field is required`;

    case ShipmentItemErrorCodes.ITEM_WEIGHT_EXCEEDS_PACKAGE_WEIGHT:
      return `the item weight ${item?.weight} exceeds the package weight of x`;

    case ShipmentConsignmentErrorCodes.INVALID_COUNTRY:
      return `invalid country code provided`;

    case ShipmentConsignmentErrorCodes.INVALID_ZIP:
      return `invalid zip provided`;

    case ShipmentConsignmentErrorCodes.INVALID_EMAIL:
      return `Invalid email provided`;

    case ShipmentConsignmentErrorCodes.INVALID_EXPORT_REASON:
      return `invalid export reason provided`;

    default:
      return errorCode;
  }
};

export const PrintPackageErrors = (
  errorCode:
    | ShipmentItemErrorCodes
    | GenericErrorCodes
    | ShipmentConsignmentErrorCodes,
  item: IShipmentItemDescription,
) => {
  switch (errorCode) {
    case ShipmentItemErrorCodes.ITEM_WEIGHT_EXCEEDS_PACKAGE_WEIGHT:
      return `The item weight ${item.weight} exceeds the package weight of x`;

    default:
      return errorCode;
  }
};

export const PrintConsignmentShipmentErrors = (
  errorCode:
    | ShipmentConsignmentErrorCodes
    | GenericErrorCodes
    | ShipmentItemErrorCodes,
  value: string | number,
) => {
  switch (errorCode) {
    case GenericErrorCodes.FIELD_REQUIRED:
      return "this field is required";

    case GenericErrorCodes.INVALID_NUMBER:
      return "this field is required";

    case GenericErrorCodes.INVALID_STRING:
      return "this field is required";

    case ShipmentConsignmentErrorCodes.INVALID_COUNTRY:
      return `Country name or code ${value} is invalid`;

    case ShipmentConsignmentErrorCodes.INVALID_EMAIL:
      return `Email ${value} is not a valid email`;

    case ShipmentConsignmentErrorCodes.INVALID_ZIP:
      return `Zip ${value} is invalid`;

    default:
      return errorCode;
  }
};

export const PrintZoneErrors = (errorCode: ZoneErrorCodes) => {
  switch (errorCode) {
    case ZoneErrorCodes.ZONE_SERVICE_NOT_AVAILABLE_FOR_ROUTE:
      return "Service not available for this shipping route.";

    default:
      return errorCode;
  }
};

export const PrintCarrierErrors = (errorCode: CarrierErrorCodes) => {
  switch (errorCode) {
    case CarrierErrorCodes.CARRIER_NOT_AVAILABLE_FOR_ROUTE:
      return "Carrier not available for route.";

    case CarrierErrorCodes.DDP_NOT_ALLOWED:
      return "DDP is not allowed on your account. Please contact your account manager.";

    default:
      return errorCode;
  }
};

export const PrintRuleErrors = (errorCode: RuleErrorCodes) => {
  switch (errorCode) {
    case RuleErrorCodes.RULE_SHIPPING_ROUTE_NOT_FOUND:
      return "No services found for this shipment. Try generating a quote or re-run rules.";

    case RuleErrorCodes.WAREHOUSE_NOT_FOUND:
      return "Warehouse not found for this shipment. Check the warehouse name matches one configured on the sales account.";

    case RuleErrorCodes.BLOCKED_SHIPMENT:
      return "This shipment matches a rule that discards new shipments.";

    case RuleErrorCodes.PUT_INTO_TO_FIX:
      return "This shipment matches a rule that puts shipments into the To Fix tab.";

    default:
      return errorCode;
  }
};

// Derived from getShipmentErrorSections so the panel's visibility can never
// disagree with its contents (a labelled shipment whose only errors are
// stale pre-label ones must not render an empty red panel).
export const hasErrors = (shipment: IShipment): boolean => {
  return getShipmentErrorSections(shipment).length > 0;
};

/**
 * Prefill for a technical support case: exactly the sections and messages
 * the Errors panel shows the user, as plain text. Used by every entry point
 * that opens the support wizard on a shipment so the autofill is identical
 * everywhere. Empty string when the shipment has no errors.
 */
export const buildShipmentErrorReport = (
  shipment: IShipment | null
): string => {
  const sections = getShipmentErrorSections(shipment);
  if (!shipment || sections.length === 0) return "";
  const reference =
    shipment.customerReference ||
    shipment.jobNumber ||
    `shipment ${shipment.id}`;
  const lines: string[] = [`Raised for ${reference}.`];
  for (const section of sections) {
    lines.push("", `${section.title}:`);
    for (const message of section.messages) {
      lines.push(`- ${message}`);
    }
  }
  return lines.join("\n").slice(0, 2000);
};

export const hasConsignmentErrors = (
  shipmentErrors: ShipmentErrors | null,
): boolean => {
  return shipmentErrors?.consignment
    ? Object.keys(shipmentErrors.consignment).length > 0
    : false;
};

export const hasLabelErrors = (packages: IShipmentPackage[]): boolean => {
  return (
    Array.isArray(packages) &&
    packages.some((p) => p?.errors && p.errors.length > 0)
  );
};

export const hasPackageErrors = (
  shipmentErrors: ShipmentErrors | null,
): boolean => {
  return (
    Array.isArray(shipmentErrors?.packages) &&
    shipmentErrors.packages.some(
      (shipmentPackage) => shipmentPackage?.isValid === false,
    )
  );
};

export const hasRuleErrors = (
  shipmentErrors: ShipmentErrors | null,
): boolean => {
  return Array.isArray(shipmentErrors?.rule) && shipmentErrors.rule.length > 0;
};

export const hasZoneErrors = (
  shipmentErrors: ShipmentErrors | null,
): boolean => {
  return Array.isArray(shipmentErrors?.zone) && shipmentErrors.zone.length > 0;
};

export const hasCarrierErrors = (
  shipmentErrors: ShipmentErrors | null,
): boolean => {
  return (
    Array.isArray(shipmentErrors?.carrier) && shipmentErrors.carrier.length > 0
  );
};
